import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { List } from 'lucide-react';
import { toast } from 'sonner';
import { usePageSize } from '../../context/PageSizeContext';

/**
 * Settings card to configure the global "rows per page" default applied to
 * every paginated list view (Invoices, Transactions, etc).
 *
 * Persisted in `app_settings` (key=`default_page_size`) via
 * `PUT /api/settings/page-size`.
 */
export const PageSizeSettingsCard = () => {
  const { pageSize, setPageSize } = usePageSize();
  const options = [25, 50, 100, 200, 500];

  const handleChange = async (val) => {
    const next = parseInt(val, 10);
    try {
      await setPageSize(next);
      toast.success(`Default page size set to ${next}`);
    } catch {
      toast.error('Failed to update page size');
    }
  };

  return (
    <Card data-testid="settings-page-size-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <List className="h-5 w-5 text-violet-500" />List Page Size
        </CardTitle>
        <CardDescription>
          Default number of rows shown on every paginated list view (Invoices, Transactions, etc).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Rows per page</Label>
            <Select value={String(pageSize)} onValueChange={handleChange}>
              <SelectTrigger data-testid="settings-page-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map(o => (
                  <SelectItem key={o} value={String(o)} data-testid={`settings-page-size-option-${o}`}>
                    {o} per page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Affects every list view across the app. Changes take effect immediately.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
